import { Component, Input, OnInit, Optional } from '@angular/core';
import { ControlContainer, UntypedFormGroup } from '@angular/forms';
import { Country, ModelDisplay, ModelMetaData, PropertyTypeCode, LoggingService } from '@fgb/core';

/**
 * Container component for the model meta data form. Use the model form helper service to build form dynamically.
 * @param formGroup - The form group to use for the form
 * @param models - The models to use to build the form
 * @param wscPrefix - The prefix to use for the translation keys
 *
 * @example
 * <fgbcl-model-meta-data-form [formGroup]="someForm" [models]="models" [wscPrefix]="wscPrefix"></fgbcl-model-meta-data-form>
 */
@Component({
  selector: 'fgbcl-model-meta-data-form',
  templateUrl: './model-meta-data-form.component.html',
  styleUrls: ['./model-meta-data-form.component.scss']
})
export class ModelMetaDataFormComponent implements OnInit {
    propertyTypeCode = PropertyTypeCode;
    regions: any[];
    display = ModelDisplay;
    today = new Date();
    private _form: UntypedFormGroup;
    private _models: ModelMetaData[] = [];
    @Input() set models(models: ModelMetaData[]){
      if( this._models?.length === 0 && models?.length > 0 ){
        this._load(models);
      }
    }
    @Input() wscPrefix = 'model.data.form';
    @Input() maxDate = { year: this.today.getFullYear() - 18, month: this.today.getMonth() + 1, day: this.today.getDate() };
    @Input() minDate = { year: this.today.getFullYear() - 100, month: this.today.getMonth() + 1, day: this.today.getDate() }
    private _load = (_: any) => {};
    onModelsLoaded = new Promise<ModelMetaData[]>(resolve => this._load = resolve);

    constructor(private logService: LoggingService,
                @Optional() private container: ControlContainer
                ){
          this.onModelsLoaded.then((models) => {
            this._initForm(models);
          });
    }


    ngOnInit(): void {
      if( !this.container ){
        this.logService.error('No form group found. Unable to create form.');
        return;
      }
    }

    /** Initialize form */
    private _initForm(models: ModelMetaData[]): void{
      
      const type = Object.prototype.toString.call(models);

      if( type !== '[object Array]' ){
        this.logService.error(`Found ${typeof models}, expecting models to be array. Unable to create form.`, models);
        return;
      }

      if( (type === '[object Array]' && models.filter(model => this._isInstaceOfModelMetaData(model)).length === 0) || !models){
        this.logService.error('No models found. Unable to create form.');
        return;
      }

      this._models = models.sort(this._sortByOrdinal());
      this._form = this.container.control as UntypedFormGroup;
    }

    /** Checks if the object is instance of ModelMetaData */
    private _isInstaceOfModelMetaData(object: any): object is ModelMetaData{
        return 'PropertyName' in object;
    }

    /** Gets the regions of the country */
    onCountryChange(event: Country): void{
      this.regions = event?.Regions;
    }

    /** Prevents using decimal in the number input */
    onNumberKeydown(event: KeyboardEvent): void{
      if(event.key==='.') event.preventDefault();
    }

    onNumberInput(event: any): void{
      event.target.value = event.target.value.replace(/[^0-9]*/g,'');
    }

    private _sortByOrdinal() {
      return (a: ModelMetaData, b: ModelMetaData) => a.Ordinal - b.Ordinal;
    }

    get models(): ModelMetaData[]{
      return this._models;
    }

    get form() : UntypedFormGroup{
      return this._form;
    }
  
}
